import React from 'react';

import { Eyebrow } from '../Eyebrow/Eyebrow';
import { DrillChevron } from '../DrillChevron/DrillChevron';
import { ACQ } from '../../theme/tokens';
import { Card, Header, TitleRow, Avatar, Title } from './DrillCard.styles';

export type DrillCardProps = {
  /** Orange overline text (e.g. "Campaign period {{period}}"). */
  eyebrow: string;
  eyebrowPlaceholders?: Record<string, string | number>;
  title: string;
  /** Optional 60px artwork shown left of the title (campaign cards). */
  image?: string;
  /** Surfaced via `event.currentTarget.dataset.id` on click. */
  id?: string;
  /** When provided, the card becomes a keyboard-operable button. */
  onOpen?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
  children?: React.ReactNode;
};

/**
 * List card with an eyebrow row, an avatar + title + drill-in chevron row and a
 * free body. Shared by the channel campaign cards and the operations promotion
 * cards; optionally clickable (mouse + Enter/Space) to open a detail page.
 */
export const DrillCard = ({ eyebrow, eyebrowPlaceholders, title, image, id, onOpen, children }: DrillCardProps): React.ReactElement => {
  const clickable = Boolean(onOpen);

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (onOpen && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onOpen(event);
    }
  };

  return (
    <Card
      $clickable={clickable}
      data-id={id}
      onClick={onOpen}
      onKeyDown={clickable ? onKeyDown : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <Header>
        <Eyebrow text={eyebrow} placeholders={eyebrowPlaceholders} color={ACQ.orange} />
        <TitleRow>
          {image ? <Avatar src={image} alt="" /> : null}
          <Title text={title} tag="span" />
          <DrillChevron />
        </TitleRow>
      </Header>
      {children}
    </Card>
  );
};

export default DrillCard;
